import { useDialog } from '@globals/dialog/useDialogStore'
import { AwardType, listAwardRecipients } from '@kudos/http-client'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Cancel from '@shared/forms/Cancel'
import FormContextWrapper from '@shared/forms/FormContextWrapper'
import Submit from '@shared/forms/Submit'
import TenantMembersAndTeamsSearchField from '@shared/forms/tenantMemberAndTeamSearchField/TenantMemberAndTeamSearchField'
import useRecipientsPreview from '@shared/hooks/useRecipientsPreview'
import { useTranslatedField } from '@shared/utils/useTranslatedField'
import { T } from '@transifex/react'
import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { useShallow } from 'zustand/shallow'
import { useAwardsRouteContext } from '../useAwardsRouteContext'
import AwardFormContent from './AwardFormContent'
import { SEND_AWARDS_MODAL_KEY } from './SendAwardsModal'
import useSendAwardForm, { SendAwardFormData } from './useSendAwardForm'
import { useSendAwardFormStore } from './useSendAwardFormStore'

type SendAwardFormProps = {
  awardType: AwardType
}
const SendAwardForm: React.FC<SendAwardFormProps> = ({ awardType }) => {
  const getTranslatedField = useTranslatedField()
  const translatedDescription = getTranslatedField(awardType, 'description')
  const location = useLocation()
  const { closeDialog } = useDialog(SEND_AWARDS_MODAL_KEY)
  const { delegatorId } = useAwardsRouteContext()
  const { setIsFormDirty } = useSendAwardFormStore(
    useShallow(state => ({
      setIsFormDirty: state.setIsFormDirty,
    })),
  )

  const { formMethods, publishAward, handleResetAwardForm, isFormDirty, qualities } =
    useSendAwardForm(awardType)
  const { control, setValue } = formMethods
  const { recipientCount, pointsRecipientCount, totalSpend, pointsBankBalance } =
    useRecipientsPreview(control, awardType.id, delegatorId)

  useEffect(() => {
    if (location.state) {
      const recipients = location.state.recipients
      if (recipients && recipients.length > 0) {
        setValue('sendTo', recipients, { shouldValidate: true, shouldDirty: true })
      }
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  useEffect(() => {
    setIsFormDirty(isFormDirty)
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty])

  const onSubmit = async (data: SendAwardFormData) => {
    await publishAward(data, () => {
      handleResetAwardForm()
      closeDialog()
    })
  }
  return (
    <FormContextWrapper
      isInDialog
      onSubmit={onSubmit}
      formMethods={formMethods}
      formActions={
        <>
          <Cancel onClick={() => closeDialog()} />
          <Submit formState={formMethods.formState} submitText={<T _str="Send" />} />
        </>
      }>
      <AwardFormContent
        awardType={awardType}
        qualities={qualities ?? []}
        header={
          <Typography
            variant="body1"
            sx={{
              mb: 2,
            }}>
            {translatedDescription}
          </Typography>
        }
        sendToSection={
          <Box
            sx={{
              mb: 2,
            }}>
            <TenantMembersAndTeamsSearchField
              control={control}
              name="sendTo"
              label={<T _str="To" />}
              searchFn={listAwardRecipients}
              showAvatars
              zIndexPlacement="modal"
              delegatorId={delegatorId}
            />
          </Box>
        }
        preview={{
          recipientCount,
          pointsRecipientCount,
          totalSpend,
          pointsBankBalance,
        }}
      />
    </FormContextWrapper>
  )
}

export default SendAwardForm
